import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Landing } from "@/components/Landing";
import { Quiz } from "@/components/Quiz";
import { Result } from "@/components/Result";
import { EmailGate } from "@/components/EmailGate";
import { SharedResult } from "@/components/SharedResult";
import { ThankYou } from "@/components/ThankYou";
import { Admin } from "@/components/Admin";
import { getResult } from "@/data/quizData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => (
  <header className="w-full max-w-xl mx-auto flex items-center justify-between py-4 px-4 sm:px-6 border-b border-[#E9DFD6]/70 mb-2 sticky top-0 z-40 bg-[#FAF7F2]/90 backdrop-blur-md">
    <a
      href="https://www.grayrootssociety.com"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="header-brand-link"
      className="flex items-center"
    >
      <img
        src="/banner.png"
        alt="Gray Roots Society — Head Up, Crown On"
        className="h-10 sm:h-12 w-auto"
      />
    </a>
    <Link
      to="/admin"
      data-testid="header-admin-link"
      className="text-xs uppercase tracking-[0.15em] font-semibold text-[#85746C] hover:text-[#B25E3B] transition-colors duration-200"
    >
      Advisor view
    </Link>
  </header>
);

const DURATION_WEIGHT = { years: 3, over_a_year: 2, few_months: 1, just_started: 0 };

const QuizFlow = () => {
  // step: -1 = landing, 0..3 = questions, 4 = result, 5 = follow-up email gate (hot leads)
  const isEmbed = new URLSearchParams(window.location.search).get("embed") === "1";
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({ q1: null, q2: null, q3: null, q4: null });
  const [submissionId, setSubmissionId] = useState(null);
  const [isHot, setIsHot] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleAnswer = async (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < 3) {
      setStep(step + 1);
      return;
    }
    const result = getResult(next.q1, next.q2);
    const hot = next.q4 >= 4 && (DURATION_WEIGHT[next.q3] || 0) >= 2;
    setIsHot(hot);
    setStep(hot ? 5 : 4);
    try {
      const res = await axios.post(`${API}/submissions`, {
        q1: next.q1,
        q2: next.q2,
        q3: next.q3,
        q4: next.q4,
        result_id: result.id,
      });
      setSubmissionId(res.data.id);
    } catch (e) {
      console.error("Failed to store submission", e);
    }
  };

  const handleBack = () => setStep((s) => Math.max(-1, s - 1));

  const result = step === 4 ? getResult(answers.q1, answers.q2) : null;

  return (
    <div className="min-h-screen w-full bg-[#FAF7F2] text-[#2C221E] flex flex-col items-center selection:bg-[#B25E3B]/20">
      {!isEmbed && <Header />}
      <main className="w-full max-w-xl mx-auto px-4 sm:px-6 py-6 flex flex-col flex-1">
        {step === -1 && <Landing onStart={() => setStep(0)} />}
        {step >= 0 && step <= 3 && (
          <Quiz step={step} onAnswer={handleAnswer} onBack={handleBack} />
        )}
        {step === 5 && (
          <EmailGate
            submissionId={submissionId}
            onDone={(sent) => {
              setEmailSent(sent);
              setStep(4);
            }}
          />
        )}
        {step === 4 && result && (
          <Result
            result={result}
            submissionId={submissionId}
            showEmailCapture={!emailSent}
            emailSent={emailSent}
          />
        )}
      </main>
      {!isEmbed && (
        <footer className="w-full max-w-xl mx-auto px-4 sm:px-6 py-6 text-center">
          <p className="text-xs text-[#85746C]">
            Head up, crown on · Gray Roots Society
          </p>
        </footer>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<QuizFlow />} />
          <Route path="/r/:id" element={<SharedResult />} />
          <Route path="/booked" element={<ThankYou />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
